import React, { useMemo } from "react";

import { AppBar, Button, Typography } from "@material-ui/core";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import queryString from "query-string";
import { Menu } from "@material-ui/icons";

import DrawerContainer from "components/DrawerContainer";
import HeaderActionsList from "components/HeaderToolbar/components/HeaderActionsList";
import LinkDropdownContainer from "components/LinkDropdownContainer";
import HeaderLink from "components/HeaderToolbar/components/HeaderLink";
import GoogleLogoutButton from "components/GoogleLogoutButton";

import toolbarRoutes from "app/routes";

import "./styles.scss";
import AuthWrapper from "components/AuthWrapper";

const HeaderToolbar = () => {
  const location = useLocation();
  const searchParams = useMemo(() => queryString.parse(location.search), [location.search]);
  const clientsList = useSelector(({ clientReducer }) => clientReducer.list) || [];

  const selectedClient = useMemo(() => {
    const clientId = searchParams.clientId;
    if (!clientId) return null;
    return clientsList.find(c => (typeof c._id === 'string' ? c._id : c._id?.toString()) === (typeof clientId === 'string' ? clientId : clientId?.toString())) || null;
  }, [clientsList, searchParams]);

  const isThemedPath = useMemo(() => {
    const p = location.pathname;
    return p === '/' || p.startsWith('/inventory/items') || p.startsWith('/inventory/categories');
  }, [location.pathname]);

  const toolbarTitle = useMemo(() => {
    if (isThemedPath && selectedClient?.code) return selectedClient.code;
    return 'Resources Management';
  }, [isThemedPath, selectedClient]);

  const titleStyles = useMemo(() => {
    if (!(isThemedPath && selectedClient?.color)) return {};

    const hex = selectedClient.color;
    if (!/^#([0-9A-Fa-f]{6})$/.test(hex)) return {};
    const r = parseInt(hex.substr(1, 2), 16) / 255;
    const g = parseInt(hex.substr(3, 2), 16) / 255;
    const b = parseInt(hex.substr(5, 2), 16) / 255;
    const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow(((c + 0.055) / 1.055), 2.4));
    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    const textColor = luminance > 0.5 ? '#000000' : '#FFFFFF';

    return { backgroundColor: selectedClient.color, padding: '0 40px', color: textColor };
  }, [isThemedPath, selectedClient]);

  return (
    <AppBar>
      <div className="header-toolbar">
        <div className="header-toolbar__menu-opener">
          <DrawerContainer
            openFrom={"left"}
            OpenerComponent={(props) => (
              <Button {...props}>
                <Menu />
              </Button>
            )}
          >
            <HeaderActionsList list={toolbarRoutes} />
          </DrawerContainer>
        </div>
        <div className="header-toolbar__title" style={titleStyles}>
          <Typography variant="h2">{toolbarTitle}</Typography>
        </div>
        <div className={"header-links header-links--left-side"}>
          {toolbarRoutes.map((route) =>
            route.type === "dropdown" ? (
              <LinkDropdownContainer
                key={route.name}
                links={route.links}
                OpenerComponent={({ thisRef, ...props }) => (
                  <HeaderLink route={route} thisRef={thisRef} {...props} />
                )}
              />
            ) : (
              <AuthWrapper permission={route.permission} key={route.name}>
                <HeaderLink route={route} />
              </AuthWrapper>
            )
          )}
        </div>
        <div className="header-links header-links--right-side">
          <GoogleLogoutButton />
        </div>
      </div>
    </AppBar>
  );
};

export default HeaderToolbar;
